import React from 'react';
import { Link } from 'react-router-dom';
import {
  Lightbulb, AlertTriangle, Info, ShieldCheck, ArrowRight, BookOpen,
} from 'lucide-react';
import { cn } from '../lib/utils';

/** Building blocks every Help topic is written with, Tailwind-only so the app theme applies. */

export const P = ({ children }) => (
  <p className="text-sm leading-relaxed text-gray-600 dark:text-gray-300">{children}</p>
);

export const SubH = ({ children }) => (
  <h3 className="mt-2 text-base font-semibold text-gray-900 dark:text-white">{children}</h3>
);

export const UL = ({ items }) => (
  <ul className="space-y-1.5 text-sm text-gray-600 dark:text-gray-300">
    {items.map((it, i) => (
      <li key={i} className="flex gap-2">
        <span className="mt-[7px] h-1.5 w-1.5 shrink-0 rounded-full bg-orange-500" />
        <span className="leading-relaxed">{it}</span>
      </li>
    ))}
  </ul>
);

/** Numbered tutorial steps — the InTutor walkthrough look. */
export const Steps = ({ steps }) => (
  <ol className="space-y-3">
    {steps.map((s, i) => (
      <li key={i} className="flex gap-3">
        <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-orange-500 text-xs font-bold text-white">
          {i + 1}
        </span>
        <div className="min-w-0 pt-0.5">
          <div className="text-sm font-medium text-gray-900 dark:text-white">{s.title}</div>
          {s.detail && <div className="mt-0.5 text-sm leading-relaxed text-gray-500 dark:text-gray-400">{s.detail}</div>}
        </div>
      </li>
    ))}
  </ol>
);

const CALLOUT = {
  tip: { icon: Lightbulb, cls: 'border-emerald-300 bg-emerald-50 text-emerald-800 dark:border-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-200', label: 'Tip' },
  warn: { icon: AlertTriangle, cls: 'border-amber-300 bg-amber-50 text-amber-800 dark:border-amber-700 dark:bg-amber-900/30 dark:text-amber-200', label: 'Watch out' },
  info: { icon: Info, cls: 'border-sky-300 bg-sky-50 text-sky-800 dark:border-sky-700 dark:bg-sky-900/30 dark:text-sky-200', label: 'Good to know' },
  admin: { icon: ShieldCheck, cls: 'border-violet-300 bg-violet-50 text-violet-800 dark:border-violet-700 dark:bg-violet-900/30 dark:text-violet-200', label: 'Needs permission' },
};

export const Callout = ({ type = 'info', children }) => {
  const c = CALLOUT[type];
  const Icon = c.icon;
  return (
    <div className={cn('flex gap-2.5 rounded-lg border p-3 text-sm leading-relaxed', c.cls)}>
      <Icon className="mt-0.5 h-4 w-4 shrink-0" />
      <div><span className="font-semibold">{c.label}: </span>{children}</div>
    </div>
  );
};

export const Tbl = ({ head, rows }) => (
  <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700">
    <table className="min-w-full text-sm">
      <thead className="bg-gray-50 dark:bg-gray-800 text-left text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">
        <tr>{head.map((h) => <th key={h} className="whitespace-nowrap px-3 py-2 font-medium">{h}</th>)}</tr>
      </thead>
      <tbody className="divide-y divide-gray-100 dark:divide-gray-700 bg-white dark:bg-gray-900">
        {rows.map((r, i) => (
          <tr key={i} className="align-top">
            {r.map((c, j) => (
              <td key={j} className={cn('px-3 py-2 leading-relaxed text-gray-600 dark:text-gray-300', j === 0 && 'font-medium text-gray-900 dark:text-white')}>
                {c}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

export const Kbd = ({ children }) => (
  <kbd className="rounded border border-gray-300 bg-gray-50 px-1.5 py-0.5 text-[11px] font-medium text-gray-600 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-300">
    {children}
  </kbd>
);

/** "Open the screen this section talks about" chip. */
export const GoTo = ({ to, label }) => (
  <Link to={to}
    className="inline-flex items-center gap-1.5 rounded-full border border-orange-300 bg-orange-50 px-3 py-1 text-xs font-medium text-gray-900 transition hover:border-orange-500 dark:border-orange-700 dark:bg-orange-900/30 dark:text-white">
    Open: {label} <ArrowRight className="h-3 w-3" />
  </Link>
);

/** Inline jargon term — dotted underline, jumps to the glossary entry. */
export const G = ({ t, children }) => (
  <Link to={`/help/glossary?term=${encodeURIComponent(t)}`}
    className="rounded-sm font-medium text-gray-900 underline decoration-orange-400 decoration-dotted underline-offset-2 hover:bg-orange-50 dark:text-white dark:hover:bg-orange-900/30"
    title={`Look up “${t}” in the glossary`}>
    {children ?? t}
  </Link>
);

/** Cross-link to another help topic. */
export const See = ({ id, label }) => (
  <Link to={`/help/${id}`}
    className="inline-flex items-center gap-1 font-medium text-gray-900 underline decoration-gray-300 underline-offset-2 hover:decoration-orange-500 dark:text-white">
    <BookOpen className="h-3.5 w-3.5" /> {label}
  </Link>
);
